import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import ContentLoader from "react-content-loader";
import { pdf } from "@react-pdf/renderer";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
  PDFViewer,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import BASE_URL from "../../../config/apiConfig";

const PDFSkeletonLoader = () => (
  <div
    style={{
      width: "80%",
      height: "auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: " rgba(243, 236, 236, 0.72)", // White background for a clean look
      borderRadius: "12px",
      padding: "25px",
      textAlign: "center",
      boxShadow: "0 6px 18px rgba(0, 0, 0, 0.1)", // Softer shadow for depth
      color: "#333",
      margin: "50px auto", // More margin for better spacing
      border: "1px solid #e0e0e0", // Light border for a subtle definition
    }}
  >
    {/* Animated Skeleton Loader */}
    <ContentLoader
      speed={2}
      width="100%"
      height={320}
      viewBox="0 0 400 320"
      backgroundColor="rgba(0, 0, 0, 0.1)"
      foregroundColor="rgba(255, 255, 255, 0.1)"
      style={{ borderRadius: "8px" }} // Slightly rounded corners for the skeleton loader
    >
      <rect x="50" y="20" rx="8" ry="8" width="300" height="30" />
      <rect x="25" y="70" rx="8" ry="8" width="350" height="160" />
      <rect x="100" y="250" rx="8" ry="8" width="200" height="20" />
    </ContentLoader>

    {/* Loading Text */}
    <p
      style={{
        marginTop: "20px",
        fontSize: "16px",
        color: "#666",
        fontFamily: "'Poppins', Arial, sans-serif",
        fontWeight: "500",
      }}
    >
      ⏳ Generating your report... This may take 1-2 minutes. Please wait.
    </p>
  </div>
);

const styles = StyleSheet.create({
  // General Page Styles
  page: {
    flexDirection: "row",
    padding: 0,
    margin: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  frame: {
    flex: 1,
    borderTopWidth: 10,
    borderLeftWidth: 20,
    borderRightWidth: 20,
    borderBottomWidth: 30,
    borderColor: "rgb(190, 244, 188)", // Outer border color
    backgroundColor: "transparent", // Outer frame background remains transparent
    position: "relative", // Parent container must be relative
    overflow: "hidden", // Ensures content stays inside the border
  },
  logoContainer: {
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  logo: {
    width: 150, // Adjusted logo size for better fit
    height: "auto", // Maintain aspect ratio
    objectFit: "contain",
  },
  mainTitle: {
    fontFamily: "Helvetica-Bold", // Explicitly set the bold font
    fontSize: 20, // Increased font size for better visibility
    fontWeight: "900", // Set to the maximum boldness for a more pronounced effect
    color: "rgba(10, 75, 161, 0.7)", // Maintained white color for contrast
    textAlign: "center", // Center align the title
    letterSpacing: 1, // Adds space between letters for a cleaner look
    textShadowColor: "rgba(10, 75, 161, 0.7)", // A subtle shadow to make the text pop
    textShadowOffset: { width: 1, height: 1 }, // Slight offset to make the shadow appear natural
    textShadowRadius: 3, // Softens the shadow for a more polished look
  },

  innerFrame: {
    flex: 1,
    padding: 10,
    borderRadius: 10,
    position: "relative",
    overflow: "hidden",
    backgroundColor: "white",
    borderWidth: 1, // Fixed border property
    borderColor: "#ddd", // Define border color separately
  },

  // Updated styles
  contentPage: {
    fontSize: 12,
    fontFamily: "Helvetica",
    lineHeight: 1.6,
    position: "relative",
    borderColor: "rgb(20, 114, 169)", // Outer border color
    backgroundColor: "rgb(190, 244, 188)",
  },
  header: {
    textAlign: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#4CAF50",
    lineHeight: 1.4,
    textAlign: "center",
  },

  contentSection: {
    padding: 10,
    marginBottom: 20,
  },
  section: {
    marginVertical: 15,
    padding: 10,
    borderBottom: "1px solid #eaeaea",
  },

  causesRecommendations: {
    flexDirection: "column",
    marginTop: 10,
  },
  footer: {
    fontSize: 8, // Smaller font size
    color: "#rgba(10, 75, 161, 0.7)", // Lighter color for the footer
    textAlign: "center", // Center align the footer text
    marginTop: 20, // Add space at the top of the page
  },
  flexContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 10,
    marginBottom: 10,
  },
  textSection: {
    flex: 0.6, // Adjust the width proportion
    marginRight: 20,
    marginTop: 10,
  },
  imageContainer: {
    flex: 0.4, // Adjust the width proportion
    alignItems: "flex-end", // Align to the right
    justifyContent: "flex-start",
  },
  imageSmall: {
    width: 150,
    height: 150,
    objectFit: "contain",
    border: "1px solid #ddd",
    borderRadius: 8,
  },
  detailsSection: {
    // marginTop: 15, // Add spacing between the sections
    // paddingHorizontal: 5,
  },
  sectionHeader: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    marginBottom: 5,
    color: "#333",
  },
  text: {
    marginBottom: 5,
    color: "#555",
    fontSize: 12,
    lineHeight: 1.5,
  },

  productContainer: {
    flexDirection: "row", // Arrange items in a row
    flexWrap: "wrap", // Allow wrapping to new rows
    justifyContent: "center", // Center the products horizontally
    gap: 0, // Space between product cards
    marginTop: 0, // Space from previous section
    padding: 20, // Padding for spacing around the container
  },
  productCard: {
    backgroundColor: "#fff", // White background for the card
    borderRadius: 12, // Rounded corners for a soft look
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow for a floating effect
    width: "30%", // Card width to allow 4 in a row
    marginBottom: 5, // Space below each card
    padding: 10, // Inner padding for card content
    textAlign: "center", // Center the text inside the card
    transition: "transform 0.3s ease, box-shadow 0.3s ease", // Smooth transition for hover effect
    cursor: "pointer", // Pointer cursor to indicate it's clickable
    border: "1px solid #ddd", // Light border for a refined look
  },
  productCardHover: {
    transform: "scale(1.05)", // Slightly scale the card when hovered
    boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.2)", // Enhanced shadow on hover
  },
  productImage: {
    alignSelf: "center", // Centers the image horizontally within its parent
    width: "100%", // Set the image width as a percentage of its parent
    aspectRatio: 1, // Maintains the aspect ratio (e.g., square images)
    resizeMode: "contain", // Ensures the image fits without cropping
    borderRadius: 8, // Rounds the corners of the image
    marginBottom: 15, // Space between the image and other elements
  },

  productTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#333",
    marginBottom: 5,
    lineHeight: 1.4,
  },
  productDescription: {
    fontSize: 10,
    color: "#555",
    marginBottom: 15,
    lineHeight: 1.6, // Improve readability
  },
  productLink: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#4CAF50", // Green link color
    textDecorationLine: "underline",
    cursor: "pointer", // Indicate that it's clickable
  },
  productHeader: {
    fontSize: 21,
    textAlign: "center",
    color: "#4CAF50", // Green link color
  },
  greenText: {
    color: "rgb(244, 12, 12)",
  },
  blueText: {
    color: "rgb(20, 114, 169)",
  },
});

const ReportPDF = ({
  data,
  screenshotUrl,
  recommendedProducts,
  diseaseInfo,
  savingCost,
}) => {
  const rupeeSymbol = "₹";
  return (
    <Document>
      {/* Page 1: Full-Size Image */}
      <Page
        size="A4"
        orientation="landscape"
        style={{
          position: "relative",
          padding: 0,
          borderColor: "rgb(190, 244, 188)",
        }}
      >
        {/* Full-Width Image with Border Clipping */}
        <View
          style={{
            position: "absolute",
            top: 20,
            left: 0,
            right: 0,
            bottom: 0,
            borderTopWidth: 40,
            borderLeftWidth: 20,
            borderRightWidth: 20,
            borderBottomWidth: 40,
            borderColor: "rgb(190, 244, 188)",
            backgroundColor: "rgb(190, 244, 188)", // Semi-transparent white background
          }}
        >
          <Image
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover", // Ensures the image fills the area while maintaining aspect ratio
              backgroundColor: "rgb(190, 244, 188)", // Background color
              borderTopLeftRadius: 10, // Top-left border radius
              borderTopRightRadius: 10, // Top-right border radius
              borderBottomLeftRadius: 20, // Bottom-left border radius
              borderBottomRightRadius: 20, // Bottom-right border radius
            }}
            src={screenshotUrl} // Replace with your actual image URL
          />
        </View>

        {/* Header Section */}
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between", // Distribute items evenly
            padding: 10,
            backgroundColor: "rgb(190, 244, 188)",
            width: "100%", // Ensure full width
          }}
        >
          {/* Logo on the Left */}
          <View
            style={{
              flex: 0.2, // Fixed width for the logo
              alignItems: "flex-start",
            }}
          >
            <Image
              style={{
                width: 150, // Adjust width as needed
                height: "auto", // Adjust height to maintain aspect ratio
                marginLeft: 12,
                objectFit: "contain",
              }}
              src="/LOGOS/Farmology-Logo-e1687720251448.png"
            />
          </View>

          {/* Main Title in the Center */}
          <View
            style={{
              flex: 0.6, // Gives more space for title
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text style={styles.mainTitle}>Personalized Farm Advisory</Text>
          </View>

          {/* Cost-Saving Section */}
          {savingCost?.total_cost > 0 && (
            <View
              style={{
                marginTop: 23,
                flex: 0.2, // More space to stand out
                alignItems: "center",
                justifyContent: "center",
                position: "relative", // Ensures the text is positioned over the image
                width: "100%",
                height: "auto", // Adjust height as needed
              }}
            >
              {/* Background Image */}
              <Image
                src="/Images/Discount Image_Blank.png"
                style={{
                  position: "absolute", // Makes it the background
                  width: "80%",
                  height: "auto",
                }}
              />

              {/* Text Over Image */}
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: "Helvetica-Bold", // Explicitly set the bold font
                  fontSize: 15, // Increased font size for better visibility
                  fontWeight: "900",
                  color: "#fff", // White color for contrast
                  textAlign: "center",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)", // Shadow for better readability
                }}
              >
                <Image
                  src="/Icons/rupee-indian (1).png "
                  style={{
                    fontSize: 10,
                    width: 10,
                    height: 10,
                    marginRight: 2,
                    marginBottom: 2,
                  }}
                />
                {savingCost?.total_cost?.toFixed(2)}/-
              </Text>
            </View>
          )}
        </View>

        <View
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: 10, // Padding for text inside footer
            backgroundColor: "rgb(190, 244, 188)", // Footer background color
            textAlign: "center", // Center-align text
          }}
        >
          <Text
            style={{
              fontSize: 8, // Smaller font size
              color: "#rgba(10, 75, 161, 0.7)", // Lighter color for the footer
            }}
          >
            DISCLAIMER: This farm advisory report is AI-generated and should be
            used as a general guide only. All recommendations should be verified
            by agricultural experts and adapted to local conditions before
            implementation.
          </Text>
        </View>
      </Page>

      {/* Page 2: Content Page */}
      <Page style={styles.contentPage} orientation="landscape">
        <View style={styles.frame}>
          <View
            style={{
              display: "flex",
              flexDirection: "row", // Arrange items in a row
              alignItems: "center", // Vertically center-align the items within the row
              justifyContent: "center", // Center the content horizontally within the row
              padding: 10, // Add padding for spacing
            }}
          >
            {/* Logo on the Left */}
            <View
              style={{
                flex: 0.2, // The logo container takes up 20% of the row width
                alignItems: "flex-start", // Align the logo to the left of its container
              }}
            >
              <Image
                style={styles.logo}
                src="/LOGOS/Farmology-Logo-e1687720251448.png" // Replace with the actual URL of the Farmology logo
              />
            </View>

            {/* Main Title in the Center */}
            <View
              style={{
                flex: -1, // The title container takes up the remaining space
                justifyContent: "center", // Center the title vertically within its container
                alignItems: "center", // Center the title horizontally
              }}
            >
              <Text style={styles.mainTitle}>Personalized Farm Advisory</Text>
            </View>
          </View>

          {/* Content Section */}
          <View style={styles.innerFrame}>
            {data?.map((item, index) => (
              <View key={index} style={styles.contentSection}>
                {/* Flex Container for Crop Details and Image */}
                <View style={styles.flexContainer}>
                  {/* Left: Text Section */}
                  <View style={styles.textSection}>
                    <Text style={styles.text}>
                      <Text
                        style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}
                      >
                        Crop Name:
                      </Text>
                      {item?.crop_name}
                    </Text>

                    <Text style={styles.text}>
                      <Text
                        style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}
                      >
                        Crop Stage:{" "}
                      </Text>
                      {item?.crop_stage}
                    </Text>
                    <Text style={styles.text}>
                      <Text
                        style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}
                      >
                        Disease:{" "}
                      </Text>
                      {item?.disease}
                    </Text>
                    <Text style={styles.text}>
                      <Text
                        style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}
                      >
                        Pests:{" "}
                      </Text>
                      {item?.pests}
                    </Text>
                  </View>

                  {/* Right: Image Container */}
                  <View
                    style={{ alignItems: "center", justifyContent: "center" }}
                  >
                    <Image
                      src={`${BASE_URL}/media/generated_images/${diseaseInfo?.imageUrl}`}
                      style={styles.imageSmall}
                    />

                    <Text
                      style={{
                        marginTop: 5, // Small spacing between the image and text
                        fontSize: 10, // Small font for subtlety
                        color: "#777", // Neutral gray for less visual weight
                        textAlign: "center", // Center-align the disclaimer
                      }}
                    >
                      *Image 1
                    </Text>
                  </View>
                </View>

                {/* Causes and Recommendations */}
                <View style={styles.detailsSection}>
                  <Text style={styles.sectionHeader}>Problem Statment:</Text>
                  <Text style={styles.text}>{diseaseInfo?.causes}</Text>
                  <Text style={{ marginVertical: 10 }}></Text>{" "}
                  {/* One line space */}
                  <Text style={styles.sectionHeader}>Solution:</Text>
                  <Text style={styles.text}>
                    {diseaseInfo?.recommendations}
                  </Text>
                  {/* One line space */}
                  <Text style={{ marginVertical: 5 }}></Text>{" "}
                  <View style={{ marginBottom: -50 }}>
                    <Text style={styles.text}>
                      Image 1 : AI-generated with DALL·E | Image created using
                      advanced AI technology.
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>

          {/* Footer */}
          <Text style={styles.footer}>
            DISCLAIMER: This farm advisory report is AI-generated and should be
            used as a general guide only. All recommendations should be verified
            by agricultural experts and adapted to local conditions before
            implementation.
          </Text>
        </View>
      </Page>

      {/* Page 3: Recommended Products */}
      <Page style={styles.contentPage} orientation="landscape">
        <View style={styles.frame}>
          <View
            style={{
              display: "flex",
              flexDirection: "row", // Arrange items in a row
              alignItems: "center", // Vertically center-align the items within the row
              justifyContent: "center", // Center the content horizontally within the row
              padding: 10, // Add padding for spacing
            }}
          >
            {/* Logo on the Left */}
            <View
              style={{
                flex: 0.2, // The logo container takes up 20% of the row width
                alignItems: "flex-start", // Align the logo to the left of its container
              }}
            >
              <Image
                style={styles.logo}
                src="/LOGOS/Farmology-Logo-e1687720251448.png" // Replace with the actual URL of the Farmology logo
              />
            </View>

            {/* Main Title in the Center */}
            <View
              style={{
                flex: -1, // The title container takes up the remaining space
                justifyContent: "center", // Center the title vertically within its container
                alignItems: "center", // Center the title horizontally
              }}
            >
              <Text style={styles.mainTitle}>Personalized Farm Advisory</Text>
            </View>
          </View>

          {/* Content Section */}
          <View style={styles.innerFrame}>
            <Text style={styles.productHeader}>Recommended Products</Text>
            <View style={styles.productContainer}>
              {recommendedProducts.length > 0 ? (
                recommendedProducts.map((product, index) => (
                  <View style={styles.productCard} key={product.id}>
                    <Image
                      style={styles.productImage}
                      source={{ uri: `${BASE_URL}${product.product_image}` }}
                    />

                    <Text style={styles.productTitle}>
                      {product.product_name}
                    </Text>
                    <Text style={styles.productDescription}>
                      {product.product_description}
                    </Text>
                    <Link style={styles.productLink} src={product.product_link}>
                      View Product
                    </Link>
                  </View>
                ))
              ) : (
                <Text>No products available.</Text> // Display a message if no products are available
              )}
            </View>
            {savingCost?.traditional_cost !== undefined &&
              savingCost?.farmology_cost !== undefined &&
              savingCost?.total_cost !== undefined && (
                <View
                  style={{
                    alignItems: "center",
                    textAlign: "center",
                    justifyContent: "center",
                    fontFamily: "Helvetica-Bold",
                    fontWeight: "bold",
                  }}
                >
                  <Text>
                    <Text style={styles.greenText}>
                      Cost of Traditional farming:{" "}
                      <Image
                        src="/Icons/rupees_red.png"
                        style={{
                          fontSize: 8,
                          width: 8,
                          height: 8,
                          marginRight: 2,
                          marginBottom: 2,
                        }}
                      />{" "}
                      {savingCost.traditional_cost.toFixed(2)}/-
                    </Text>{" "}
                    |{" "}
                    <Text style={styles.blueText}>
                      Cost of Farmology farming:{" "}
                      <Image
                        src="/Icons/rupees_blue.png"
                        style={{
                          fontSize: 8,
                          width: 8,
                          height: 8,
                          marginRight: 2,
                          marginBottom: 2,
                        }}
                      />{" "}
                      {savingCost.farmology_cost.toFixed(2)}/-
                    </Text>{" "}
                    |{" "}
                    <Text style={styles.greenText}>
                      YOU ARE SAVING APPROX:{" "}
                      <Image
                        src="/Icons/rupees_red.png"
                        style={{
                          fontSize: 8,
                          width: 8,
                          height: 8,
                          marginRight: 2,
                          marginBottom: 2,
                        }}
                      />
                      {savingCost.total_cost.toFixed(2)}
                      /-
                    </Text>
                  </Text>
                </View>
              )}

            <View
              style={{
                alignItems: "center",
                textAlign: "center",
                justifyContent: "center",
                marginBottom: -10,
              }}
            >
              <Text>
                *Farmers using Farmology can cut input costs, boost yield, and
                reduce waste. Savings depend on farm size, crop, inputs, and
                conditions.
              </Text>
            </View>
          </View>

          {/* Footer */}
          <Text style={styles.footer}>
            DISCLAIMER: This farm advisory report is AI-generated and should be
            used as a general guide only. All recommendations should be verified
            by agricultural experts and adapted to local conditions before
            implementation.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const GenerateReport = () => {
  const location = useLocation();
  const { sowingDate, cropName, humidity, fieldId, landSize } =
    location.state || {};

  const [data, setData] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [diseaseInfo, setDiseaseInfo] = useState({
    recommendations: "",
    causes: "",
    imageUrl: "",
  });
  const [savingCost, setSavingCost] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPDF, setShowPDF] = useState(false);
  const [buttonText, setButtonText] = useState(
    "Generate Customized Farm Advisory Report"
  );
  const [createdAt, setCreatedAt] = useState(null);
  const [reportGeneratedDate, setReportGeneratedDate] = useState(null);
  const [nextAvailableDate, setNextAvailableDate] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Function to fetch all necessary data before generating the report
  const fetchData = async () => {
    setLoading(true);
    setButtonText("Generating Report...");

    try {
      // Make multiple API calls in parallel
      const [screenshotResponse, mainDataResponse] = await Promise.all([
        axios.post(`${BASE_URL}/api/user/generate-pdf/generate-screenshort`, {
          UID: process.env.REACT_APP_UID,
          FieldID: fieldId,
          Crop: cropName,
        }),
        axios.post(`${BASE_URL}/api/user/generate-pdf/fetch-data`, {
          cropName,
          humidity,
          sowingDate,
        }),
      ]);

      let screenshotUrl = null;
      if (screenshotResponse.data.screenshotName) {
        screenshotUrl = `${BASE_URL}/media/screenshots/${screenshotResponse.data.screenshotName}`;
        setScreenshotUrl(screenshotUrl);
      }

      const mainData = mainDataResponse.data;
      setData(mainData);

      let diseaseInfo = { recommendations: "", causes: "", imageUrl: "" };
      let savingCost = null;
      let recommendedProducts = [];

      // If data exists, fetch disease info, saving costs, and recommended products
      if (mainData.length > 0) {
        const diseaseName = mainData[0]?.disease || "Unknown Disease";
        const pestName = mainData[0]?.pests || "Unknown Pests";
        const pests_spray_irrigation_cost =
          mainData[0]?.pests_spray_irrigation_cost || 0;
        const disease_spray_irrigation_cost =
          mainData[0]?.disease_spray_irrigation_cost || 0;
        const irrigation_cost = mainData[0]?.irrigation_cost || 0;

        try {
          // Fetch other necessary data in parallel
          const [imageResponse, savingCostResponse, productsResponse] =
            await Promise.all([
              axios.post(
                `${BASE_URL}/api/user/generate-pdf/generate-text-and-image`,
                {
                  cropName,
                  diseaseName,
                  pestName,
                }
              ),
              axios.post(
                `${BASE_URL}/api/user/generate-pdf/calculate-saving-cost`,
                {
                  UID: process.env.REACT_APP_UID,
                  FieldID: fieldId,
                  pests_spray_irrigation_cost,
                  disease_spray_irrigation_cost,
                  irrigation_cost,
                  landSize,
                  crop: cropName,
                }
              ),
              axios.post(
                `${BASE_URL}/api/user/generate-pdf/recommended-products`,
                {
                  productIds: mainData.reduce(
                    (acc, item) => acc.concat(item.products),
                    []
                  ),
                }
              ),
            ]);

          // Set Disease Info
          if (imageResponse.data.success) {
            diseaseInfo = {
              recommendations: imageResponse.data.recommendations,
              causes: imageResponse.data.causes,
              imageUrl: imageResponse.data.image_name,
            };
            setDiseaseInfo(diseaseInfo);
          }

          // Set Saving Cost
          savingCost = savingCostResponse.data;
          setSavingCost(savingCost);

          // Set Recommended Products
          recommendedProducts = productsResponse.data;
          setRecommendedProducts(recommendedProducts);
        } catch (innerError) {
          console.error("Error fetching additional data:", innerError.message);
          setLoading(false);
          setButtonText("Please Retry");
          return null;
        }
      }

      setLoading(false);
      setButtonText("Report Generated");
      return {
        mainData,
        screenshotUrl,
        diseaseInfo,
        savingCost,
        recommendedProducts,
      };
    } catch (error) {
      console.error("Error fetching data:", error.message);
      setLoading(false);
      setButtonText("Please Retry");
      return null;
    }
  };

  useEffect(() => {
    fetchFieldDates();
  }, [fieldId]);

  const fetchFieldDates = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/user/generate-pdf/field-dates/${fieldId}`
      );
      if (response.data.created_at) {
        setCreatedAt(new Date(response.data.created_at));
      }
      if (response.data.report_generated_date) {
        setReportGeneratedDate(new Date(response.data.report_generated_date));
      }
      if (response.data.pdf_url) {
        setPdfUrl(`${BASE_URL}${response.data.pdf_url}`);
      }
      checkReportEligibility(
        response.data.created_at,
        response.data.report_generated_date
      );
    } catch (error) {
      console.error("Error fetching field data:", error);
    }
  };

  const checkReportEligibility = (created_at, report_generated_date) => {
    if (!created_at) return;

    const today = new Date();
    const createdDate = new Date(created_at);
    let nextDate = null;
    let showGenerateButton = false;

    if (!report_generated_date) {
      // If no report generated yet
      const diffInDays = Math.floor(
        (today - createdDate) / (1000 * 60 * 60 * 24)
      );

      if (diffInDays >= 3) {
        showGenerateButton = true;
        setButtonText("Generate Customized Farm Advisory Report");
      } else {
        nextDate = new Date(createdDate);
        nextDate.setDate(createdDate.getDate() + 3);
      }
    } else {
      // If report already generated
      const lastReportDate = new Date(report_generated_date);
      const diffInDays = Math.floor(
        (today - lastReportDate) / (1000 * 60 * 60 * 24)
      );

      if (diffInDays >= 5) {
        showGenerateButton = true;
        setButtonText("Regenerate Report");
      } else {
        nextDate = new Date(lastReportDate);
        nextDate.setDate(lastReportDate.getDate() + 5);
      }
    }

    setShowButton(showGenerateButton);
    setNextAvailableDate(nextDate);
  };

  // Function to generate the PDF after fetching all data
  const handleGeneratePDF = async () => {
    setLoading(true);
    setShowPDF(false);
    setButtonText("Generating Report...");

    try {
      // Step 1: Fetch data
      const fetchedData = await fetchData();

      if (
        !fetchedData ||
        !fetchedData.mainData ||
        fetchedData.mainData.length === 0
      ) {
        console.error("No valid data available for PDF generation.");
        setButtonText("Generate Customized Farm Advisory Report");
        return;
      }

      // Extract necessary data
      const {
        mainData,
        screenshotUrl,
        recommendedProducts,
        diseaseInfo,
        savingCost,
      } = fetchedData;

      // Step 2: Validate screenshot URL
      const validScreenshotUrl =
        screenshotUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(screenshotUrl)
          ? screenshotUrl
          : null;

      console.log("Fetched Data:", fetchedData);

      // Step 3: Generate PDF document
      const doc = (
        <ReportPDF
          data={mainData}
          screenshotUrl={validScreenshotUrl}
          recommendedProducts={recommendedProducts}
          diseaseInfo={diseaseInfo}
          savingCost={savingCost}
        />
      );

      if (!doc) throw new Error("PDF document creation failed.");

      const blob = await pdf(doc).toBlob();
      if (!blob) throw new Error("Failed to generate PDF Blob.");

      console.log("PDF Blob created:", blob);

      // Step 4: Upload PDF to backend
      const formData = new FormData();
      formData.append("pdf", blob, `${fieldId}_Farmology_Report.pdf`);
      formData.append("fieldId", fieldId);

      let isPDFSaved = false;

      try {
        const uploadResponse = await axios.post(
          `${BASE_URL}/api/user/generate-pdf/save-pdf`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (uploadResponse.status === 200) {
          console.log("PDF saved successfully.");
          isPDFSaved = true;
        } else {
          throw new Error("Failed to upload PDF to server.");
        }
      } catch (uploadError) {
        console.error("Error saving PDF:", uploadError.message);
      }

      // Step 5: Update report generation date only if PDF is saved
      if (isPDFSaved) {
        try {
          await axios.post(
            `${BASE_URL}/api/user/generate-pdf/update-report-date/${fieldId}`
          );
          console.log("Report generation date updated successfully.");
        } catch (dateError) {
          console.error("Error updating report date:", dateError.message);
        }
      }

      // Step 6: Show PDF on UI
      setLoading(false);
      setShowPDF(true);
    } catch (error) {
      console.error("Error during report generation:", error.message);
    } finally {
      setLoading(false); // Always stop the spinner
      setButtonText("Regenerate Report");
    }
  };

  return (
    <div
      style={{
        textAlign: "center",
        margin: "50px auto",
        padding: "0 10px",
        maxWidth: "900px",
      }}
    >
      {/* If a report is already generated, show the next available date */}
      {nextAvailableDate && (
        <p style={{ fontSize: "16px", color: "#888", marginTop: "20px" }}>
          You can generate the next report on{" "}
          <strong>{nextAvailableDate.toDateString()}</strong>.
        </p>
      )}

      {/* If an old PDF exists, show the generated date and download link */}
      {pdfUrl && reportGeneratedDate && (
        <div
          style={{
            marginTop: "20px",
            padding: "10px",
            backgroundColor: "#f9f9f9",
            borderRadius: "6px",
            border: "1px solid #ddd",
          }}
        >
          <p style={{ fontSize: "16px", color: "#333", fontWeight: "bold" }}>
            Last Generated Report:{" "}
            {new Date(reportGeneratedDate).toDateString()}
          </p>
          <a
            href={pdfUrl}
            download="Previous-Farmology-Report.pdf"
            style={{
              display: "inline-block",
              marginTop: "10px",
              padding: "8px 16px",
              fontSize: "14px",
              backgroundColor: "#007BFF",
              color: "#fff",
              borderRadius: "4px",
              textDecoration: "none",
            }}
          >
            Download Previous Report
          </a>
        </div>
      )}

      {/* Show the generate button only if a new report can be generated */}
      {showButton ? (
        <button
          onClick={handleGeneratePDF}
          style={{
            padding: "12px 24px",
            fontSize: "16px",
            color: "#fff",
            backgroundColor: "#4CAF50",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            opacity: loading ? 0.5 : 1,
            width: "100%",
            maxWidth: "300px",
            marginTop: "20px",
          }}
          disabled={loading}
        >
          {loading ? "Generating Report..." : buttonText}
        </button>
      ) : null}

      {/* Loader while PDF is generating */}
      {loading && <PDFSkeletonLoader style={{ marginTop: "10px" }} />}

      {/* PDF Display Section */}
      {showPDF && !loading && data.length > 0 && (
        <>
          {/* PDF Download Link (Always Visible) */}
          <PDFDownloadLink
            document={
              <ReportPDF
                data={data}
                screenshotUrl={screenshotUrl}
                recommendedProducts={recommendedProducts}
                diseaseInfo={diseaseInfo}
                savingCost={savingCost}
              />
            }
            fileName="Farmology-Report.pdf"
            style={{
              display: "block",
              margin: "20px auto",
              textAlign: "center",
              color: "#007BFF",
              fontWeight: "bold",
              textDecoration: "none",
              fontSize: "16px",
            }}
          >
            {({ loading }) =>
              loading ? "It will take a few seconds..." : "Download PDF"
            }
          </PDFDownloadLink>

          {/* PDF Viewer (Only for Tablets & Larger) */}
          <div
            style={{
              width: "100%",
              maxWidth: "100%",
              margin: "20px auto",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <PDFViewer
              width="100%"
              height={600}
              style={{
                borderRadius: "10px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                maxWidth: "100%",
              }}
            >
              <ReportPDF
                data={data}
                screenshotUrl={screenshotUrl}
                recommendedProducts={recommendedProducts}
                diseaseInfo={diseaseInfo}
                savingCost={savingCost}
              />
            </PDFViewer>
          </div>
        </>
      )}

      {/* No Data Available */}
      {showPDF && !loading && data.length === 0 && (
        <p style={{ fontSize: "16px", color: "#888", marginTop: "20px" }}>
          No data available for the given conditions.
        </p>
      )}
    </div>
  );
};

export default GenerateReport;
